// Generate a `cloudgrid.yaml` for the `plug` auto-init path (Option E).
//
// Unlike `grid init --type <T>`, auto-init does NOT scaffold starter files
// into the user's directory — they already have an app. The generated yaml
// declares one `web` service of the detected type, sourced from the project
// root (`source.path: .`, decision 124), so the existing files are what the
// tarball packs. The service gets `path: /` so it passes the backlog-082
// routability check and serves at the outlet root.

import { readFileSync, existsSync, writeFileSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import { dirname, join } from 'node:path';

import type { DetectedBuild, DetectedType } from './framework-detect.js';

// Read the annotated template's reference sections (everything from the first
// `## ───` divider onward) and return as a trailing comment block. Appended to
// the gridify-generated yaml so the no-arg init output is also "full commented."
function annotatedSurface(): string {
  const here = dirname(fileURLToPath(import.meta.url));
  const candidates = [
    join(here, '../templates/cloudgrid.annotated.yaml'),      // dist/
    join(here, '../../templates/cloudgrid.annotated.yaml'),   // src/services/
  ];
  for (const p of candidates) {
    if (existsSync(p)) {
      const raw = readFileSync(p, 'utf8');
      const idx = raw.indexOf('## ─');
      return idx === -1 ? '' : '\n' + raw.slice(idx);
    }
  }
  return '';
}

export interface GeneratedYamlSpec {
  /** Entity slug (the server-canonical name). */
  name: string;
  /** Detected service type. */
  type: DetectedType;
  /** Server-side build step for static-build frameworks (Vite / Astro /
   *  SvelteKit / Nuxt). Omitted for plain static sites, node, nextjs, python. */
  build?: DetectedBuild;
  /**
   * Top-level substrate needs to declare (BL621 gridify — Decision 042 Mode 1
   * §init behavior b). Each entry is a cloudgrid.yaml v2 need-word (database /
   * cache / vector / …) emitted as `<need>: true`. Omitted entirely when empty,
   * so the `plug` auto-init path — which never passes it — keeps its exact
   * output. Order is preserved as given.
   */
  needs?: string[];
  /**
   * Which command produced this yaml — drives the header comment only.
   * Defaults to `'plug'` so the existing auto-init output is byte-for-byte
   * unchanged; gridify passes `'init'`, `grid new` passes `'new'`.
   */
  generatedBy?: 'plug' | 'init' | 'new';
  /**
   * Whether the single `web` service packs from the project root
   * (`source: path: .`). True for the "adopt my existing files" paths (plug
   * auto-init, gridify). `grid new` scaffolds starter files into
   * `services/web/`, which is the default source, so it passes `false` and no
   * `source:` override is written. Defaults to `true` — existing callers keep
   * their exact output.
   */
  sourceFromRoot?: boolean;
  /**
   * Emit a top-level `agent:` block (an app-is-an-agent metadata flip, not a
   * separate type — Decision 012 / non-negotiable product rules). Used by
   * `grid new --agent`. Defaults to false.
   */
  agent?: boolean;
}

/** Build the yaml text. Exposed for unit assertions. */
export function buildGeneratedYaml(spec: GeneratedYamlSpec): string {
  const sourceFromRoot = spec.sourceFromRoot ?? true;
  const by = spec.generatedBy ?? 'plug';
  const header = sourceFromRoot
    ? `# One service, sourced from this directory. Edit freely.`
    : `# One service scaffolded under services/web/. Edit freely.`;
  const lines: string[] = [
    `# Generated by \`grid ${by}\`${by === 'new' ? '.' : ' auto-init.'}`,
    header,
    `name: ${spec.name}`,
  ];
  // Top-level needs block (gridify). Sits between `name:` and `services:` so it
  // applies to the whole app, matching the v2 hybrid-scope default.
  if (spec.needs && spec.needs.length > 0) {
    lines.push(`needs:`);
    for (const need of spec.needs) {
      lines.push(`  ${need}: true`);
    }
  }
  lines.push(
    `services:`,
    `  web:`,
    `    type: ${spec.type}`,
    `    path: /`,
  );
  if (spec.build) {
    lines.push(
      `    build:`,
      `      command: ${spec.build.command}`,
      `      output: ${spec.build.output}`,
    );
  }
  if (sourceFromRoot) {
    lines.push(
      `    source:`,
      `      path: .`,
    );
  }
  // Top-level `agent:` block — an app with an agent: block IS an agent (same
  // deploy path). `grid new --agent` opts in.
  if (spec.agent) {
    lines.push(
      `agent:`,
      `  trigger: webhook`,
    );
  }
  lines.push(``);
  return lines.join('\n') + annotatedSurface();
}

/** Write `cloudgrid.yaml` into `dir`. Caller guarantees no yaml exists yet. */
export function writeGeneratedYaml(dir: string, spec: GeneratedYamlSpec): void {
  writeFileSync(join(dir, 'cloudgrid.yaml'), buildGeneratedYaml(spec), 'utf-8');
}
